import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { concat, filter as lFilter } from 'lodash-es';
import { Observable, Subject, filter, takeUntil, tap } from 'rxjs';
import { DialogService } from '~app/app/~shared/services/dialog.service';
import { UserManagementService } from '~app/app/~shared/services/user-management.service';
import { isIncludedInArray } from '~app/app/~shared/utils/utils';
import { ResourceFacade } from '~app/app/~store/facades/resource.facade';
import { User, UserRole } from '~core/models/auth.models';
import { EventService } from '../../../~shared/services/event.service';
import { AdminCreationFormComponent } from '../../modals/admin-creation-form/admin-creation-form.component';

@Component({
  selector: 'user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserListComponent implements OnDestroy {
  @Input() excludedRoles?: string[] | null;
  elements$ = this.resourceFacade.resources$ as Observable<User[]>;

  currentError$ = this.resourceFacade.currentError$;
  paginationData$ = this.resourceFacade.paginationData$;
  isLoading$ = this.resourceFacade.isLoading$;

  onGlobalRefresh$ = this.eventService.events$.pipe(
    filter(ev => ev.type === 'REFRESH_DATA'),
    tap(() => this.refreshData())
  );

  loadingItemIds: string[] = [];
  includes = isIncludedInArray;
  userRole = UserRole;

  private _destroy$ = new Subject<void>();

  constructor(
    private resourceFacade: ResourceFacade,
    private userService: UserManagementService,
    private eventService: EventService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  refreshData(): void {
    this.resourceFacade.getData({ refresh: true });
  }

  handlePageEvent(e: PageEvent): void {
    this.resourceFacade.setPagination({
      ...(e.pageSize && { itemPerPage: e.pageSize }),
      page: e.pageIndex + 1 || 1
    });
  }

  toggleLockState(user: User): void {
    this.loadingItemIds = concat([...this.loadingItemIds, user.id]);
    this.userService
      .toggleLockState(user)
      .pipe(
        takeUntil(this._destroy$),
        tap({
          finalize: () => {
            this.loadingItemIds = lFilter(
              this.loadingItemIds,
              el => el !== user.id
            );
          }
        })
      )
      .subscribe();
  }

  editUser(user: User): void {
    const dialogRef = this.dialogService.open({
      component: AdminCreationFormComponent,
      config: {
        panelClass: ['mat-dialog-custom-full-screen'],
        disableClose: true,
        height: '100vh',
        width: '100vw',
        maxWidth: '100vw',
        data: user
      }
    });

    dialogRef.componentRef?.setInput('user', user);
  }

  delete(user: User): void {
    this.userService.delete(user).pipe(takeUntil(this._destroy$)).subscribe();
  }
}
